import { config } from "../config.js";
import { openModalWindow } from "../utils/open-modal-window.js";

/**
 * Collection of helpers that make it easier to connect with the Microsoft Graph
 * Apis that are required in order for the backend to be able to export files to
 * OneDrive.
 */
// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace MicrosoftApi {
  /**
   * Keeps track of whether or not the client has attempted to authorize
   * access to the user's account yet.
   */
  let attemptedAuthorization = false;

  /**
   * Keeps track of the user's current access token, which we're going to use
   * to authenticate with the user's Microsoft Account from the backend.
   */
  let accessToken: string | undefined;
  let accessTokenExpiresAt: number | undefined;

  /**
   * Contains a list of listeners that's waiting for the initial attempt at
   * authorizing to the user's Google Account to complete.
   */
  const initialAuthorizationCallbacks: Array<() => void> = [];

  /**
   * Contains the list of scopes required in order for the application to work
   * as expected.
   */
  const scopes = [
    "files.readwrite",
    "notes.readwrite",
    "offline_access",
    "user.read",
  ];

  /**
   * Helper that'll bind a listener to the initial authorization callback,
   * which is an easy way for third party applications to know when usage of
   * the MicrosoftApi is ready.
   */
  export function onInitialAuthorization(callback: () => void): void {
    // If the client has already attempted to authorize access, then trigger
    // the callback immediately
    if (attemptedAuthorization) {
      callback();

      return;
    }

    // If we get here, we're still waiting for the initial authorization to
    // finish - register the callback
    initialAuthorizationCallbacks.push(callback);
  }

  /**
   * Helper that determines if the user has given us access to his Microsoft
   * Account.
   */
  export function isAuthorized(): boolean {
    return !!accessToken && !!accessTokenExpiresAt;
  }

  /**
   * Helper that attempts to authorize access to the user's Microsoft Account.
   */
  export async function authorize(
    onSuccess?: () => void,
    onError?: () => void,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // Create a unique id for the attempt to authorize the user
      const uniqueId = Date.now();

      // This variable will contain the response from the modal window, as
      // received through the postMessage events
      let modalResponse:
        | undefined
        | Error
        | { accessToken: string; expiresAt: number };

      // Listener that'll wait for any messages originating from the modal
      // window, so we know whether the authorization was successful or
      // not
      const onMessage = (evt: MessageEvent) => {
        const data = evt.data as {
          sender?: string;
          error?: string;
          data?: string;
        };

        // Bail out, if this was not the event we are waiting for
        if (
          !data ||
          data.sender !== `gExport.MicrosoftApi.authorize.${uniqueId}`
        ) {
          return;
        }

        // ... Otherwise cache the response
        if (data.error) {
          modalResponse = new Error(data.error);
        } else {
          try {
            modalResponse = data.data
              ? (JSON.parse(data.data) as {
                  accessToken: string;
                  expiresAt: number;
                })
              : undefined;
          } catch (err) {
            if (err instanceof Error) {
              modalResponse = err;
            } else {
              modalResponse = new Error(String(err));
            }
          }
        }
      };

      // Subscribe our event listener
      window.addEventListener("message", onMessage);

      // Prepare the url for opening the popup window
      const popupUrl = [
        "https://login.microsoftonline.com/common/oauth2/v2.0/authorize",
        `?client_id=${encodeURIComponent(config.microsoftApi.appId)}`,
        `&redirect_uri=${encodeURIComponent(config.microsoftApi.redirectUrl)}`,
        `&scope=${encodeURIComponent(scopes.join(" "))}`,
        "&response_type=code",
        "&response_mode=fragment",
        `&state=${uniqueId}`,
        "&prompt=login",
      ].join("");

      // Open the popup modal and wait for the user to authorize the
      // application
      openModalWindow(popupUrl, { width: 483, height: 600 })
        .then(
          () => {
            // If we get here, and no response have been received from the
            // modal window, then the user closed it manually - reject the
            // external promise
            if (!modalResponse) {
              reject(new Error("User cancelled authorization."));

              if (onError) {
                onError();
              }

              // ... If an error was thrown by the modal window, then reject
              // the external promise with this error
            } else if (modalResponse instanceof Error) {
              reject(modalResponse);

              if (onError) {
                onError();
              }

              // ... If the response was not an access token, then reject the
              // external promise with a type error
            } else if (
              !modalResponse ||
              typeof modalResponse.accessToken !== "string" ||
              typeof modalResponse.expiresAt !== "number"
            ) {
              reject(
                new TypeError("Authorization resvoled with a non-token value."),
              );

              if (onError) {
                onError();
              }

              // ... Otherwise everything went according to plan, cache the
              // user's access token and resolve the promise!
            } else {
              accessToken = modalResponse.accessToken;
              accessTokenExpiresAt = modalResponse.expiresAt;
              resolve();

              if (onSuccess) {
                onSuccess();
              }
            }
          },
          (reason) => {
            // If we get here, an error occurred while opening the desired
            // popup window, so reject the external promise
            reject(reason);

            if (onError) {
              onError();
            }
          },
        )
        .then(() => {
          window.removeEventListener("message", onMessage);
        });
    });
  }

  /** Helper that gets the access token of the currently signed in user */
  export async function getAccessToken(): Promise<string | undefined> {
    // Bail out if no access token is available to the user
    if (!accessToken || !accessTokenExpiresAt) {
      return;
    }

    // Once we get here, return the access token that's kept within the
    // browser memory
    return accessToken;
  }

  /** Specification of the response format for the getUserData helper */
  export type UserData = {
    id: string;
    mail: string;
    name: string;
  };

  /** Helper that fetches data about the currently signed in user */
  export async function getUserData(
    callback?: (data: UserData | { error: Error }) => void,
  ): Promise<UserData> {
    try {
      // Get the access token that we're going to use to interact with
      // the Microsoft Graph api!
      const currAccessToken = await getAccessToken();

      // Bail out if the user has not been authorized!
      if (!currAccessToken) {
        throw new Error(
          "gExport.MicrosoftApi.getUserData(): User must be authorized!",
        );
      }

      // Submit the request to Microsoft Graph API
      const req = await fetch("https://graph.microsoft.com/v1.0/me", {
        headers: {
          Authorization: `Bearer ${currAccessToken}`,
        },
      });

      if (!req.ok) {
        throw new Error(
          `gExport.MicrosoftApi.getUserData(): fetch error: ${req.status} ${req.statusText}\n\n${await req.text()}`,
        );
      }

      // Stringly type response data, using internal specification
      const data = (await req.json()) as {
        businessPhones: string[];
        displayName: string;
        givenName: string;
        id: string;
        jobTitle?: string;
        mail?: string;
        mobilePhone?: string;
        officeLocation?: string;
        preferredLanguage?: string;
        surname: string;
        userPrincipalName: string;
      };

      // Prepare well-formatted output
      const output = {
        id: data.id,
        mail: data.mail || data.userPrincipalName,
        name: data.displayName,
      };

      callback?.(output);
      return output;
    } catch (err) {
      callback?.({ error: err as Error });
      throw err;
    }
  }

  /**
   * Specification of the response format when accessing a specific file's
   * data.
   */
  export type FileData = {
    createdAt: string;
    fileId: string;
    fileName: string;
    folderId: string;
    modifiedAt: string;
    url: string;
  };

  /** Helper that fetches data about a single file */
  export async function getFileData(
    fileId: string,
    callback?: (value: FileData | { error: Error }) => void,
  ): Promise<FileData> {
    try {
      // Get the access token that we're going to use to interact with
      // the Microsoft Graph api!
      const currAccessToken = await getAccessToken();

      // Bail out if the user has not been authorized!
      if (!currAccessToken) {
        throw new Error(
          "gExport.MicrosoftApi.getFileData(): User must be authorized!",
        );
      }

      // Submit the request to Google Drive
      const req = await fetch(
        `https://graph.microsoft.com/v1.0/me/drive/items/${fileId}`,
        {
          headers: {
            Authorization: `Bearer ${currAccessToken}`,
          },
        },
      );

      if (!req.ok) {
        throw new Error(
          `gExport.MicrosoftApi.getFileData(): fetch error: ${req.status} ${req.statusText}\n\n${await req.text()}`,
        );
      }

      // Parse the JSON-response
      const parsedResponse = (await req.json()) as {
        createdDateTime: string;
        lastModifiedDateTime: string;
        name: string;
        parentReference: { id: string };
        webUrl: string;
      };

      // Create well-formatted file data object
      const fileData: FileData = {
        createdAt: parsedResponse.createdDateTime,
        fileId,
        fileName: parsedResponse.name,
        folderId: parsedResponse.parentReference.id,
        modifiedAt: parsedResponse.lastModifiedDateTime,
        url: parsedResponse.webUrl,
      };

      callback?.(fileData);
      return fileData;
    } catch (err) {
      callback?.({ error: err as Error });
      throw err;
    }
  }

  /** Internal specification of the value returned by the openPicker method */
  export type PickerResponse = {
    action: string;
    docs: Array<{ id: string }>;
  };

  /**
   * Helper that opens the Google Drive file picker, from which the user can
   * choose a position where he wants to upload his file to.
   */
  export async function openPicker(): Promise<PickerResponse> {
    return new Promise<PickerResponse>((resolve, reject) => {
      reject(
        new Error(
          "gExport.MicrosoftApi.openPicker(): OneDrive picker is currently not supported!",
        ),
      );
    });
  }

  /**
   * Use this method to sign the currently logged in user out of his Microsoft
   * Account!
   */
  export async function signOut(): Promise<void> {
    // Reset internal status
    accessToken = undefined;
  }

  /**
   * Helper that'll run once every 100ms, and determine if the user's current
   * access token has expired - and in that case fetch a new one...
   */
  function checkAccessTokenStatus(): void {
    // Bail out if the user isn't authorized...
    if (!accessToken || !accessTokenExpiresAt) {
      return;
    }

    // ... Otherwise determine if the user's current access token has
    // expired (and in that case, attempt to fetch an updated one from the
    // backend)
    if (accessTokenExpiresAt < Date.now()) {
      accessToken = undefined;
      accessTokenExpiresAt = undefined;
    }
  }

  /**
   * Initializes the MicrosoftApi by making sure that the JavaScript library
   * has been loaded and checking if the user has already authorized
   * access to the api.
   */
  async function init(): Promise<void> {
    // Start an interval that'll automatically refresh access tokens when
    // they've expired
    setInterval(checkAccessTokenStatus, 100);

    // Register that we've attempted to handle initial authorization
    attemptedAuthorization = true;

    // Loop through and trigger all listeners of the initial
    // authorization attempt
    initialAuthorizationCallbacks.forEach((callback) => {
      callback();
    });

    // Clean up memory by removing all stored callbacks from memory
    initialAuthorizationCallbacks.splice(0);
  }

  /**
   * This method should *only* be triggered by gateways/ms.html, as it will
   * attempt to parse response given from the Microsoft Azure Active Directory
   * after a user has been redirected back to our application.
   */
  export async function handleGateway(): Promise<void> {
    // Extract parameters given from Microsoft from hash fragment
    const query = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const opener = window.opener as Window;

    // If an error has occurred, then propagate it to the parent window
    if (query.has("error")) {
      opener.postMessage(
        {
          error: `${query.get("error")}: ${query.get("error_description")}`,
          sender: `gExport.MicrosoftApi.authorize.${query.get("state")}`,
        },
        "*",
      );

      // ... If no authorization code was given, then propagate an error to
      // the parent window notifying it of this issue
    } else if (!query.has("code")) {
      opener.postMessage(
        {
          error: "No authorization code was given.",
          sender: `gExport.MicrosoftApi.authorize.${query.get("state")}`,
        },
        "*",
      );

      // ... Otherwise we've received an authorization code, which we'll need
      // to prompt the ExportService backend to exchange for an access_token
    } else {
      try {
        // Reach out to the backend, in order to exchange the given
        // authorization code for an access_token
        const token = await exchangeAuthorizationCode({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          code: query.get("code")!,
          redirectUrl: `${config.baseUrl}/msgraph/oauth/callback`,
        });

        // Submit the accessToken to the parent window
        opener.postMessage(
          {
            data: JSON.stringify(token),
            sender: `gExport.MicrosoftApi.authorize.${query.get("state")}`,
          },
          "*",
        );
      } catch (err) {
        // If an error occurred while reaching out to the api, then
        // submit this error as the response of the Gateway
        if (err instanceof Error) {
          opener.postMessage(
            {
              error: err.message,
              sender: `gExport.MicrosoftApi.authorize.${query.get("state")}`,
            },
            "*",
          );
        } else {
          opener.postMessage(
            {
              error: String(err),
              sender: `gExport.MicrosoftApi.authorize.${query.get("state")}`,
            },
            "*",
          );
        }
      }
    }

    window.close();
  }

  /**
   * Helper that exchanges an authorization code from the Microsoft Azure
   * Active Directory v2 for an access_token, which we can use to connect with
   * other Microsoft endpoints.
   */
  export async function exchangeAuthorizationCode(data: {
    code: string;
    redirectUrl: string;
  }): Promise<{ accessToken: string; expiresAt: number }> {
    // Fetch the access token from the remote server
    const response = await fetch(`${config.baseUrl}/msgraph/oauth/authorize`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-Security-Method": !config.baseUrl.includes("localhost")
          ? "amazon.export.gyldendal.dk"
          : "",
      },
      body: JSON.stringify(data),
    });

    // Parse the json based response, and return the actual access token
    return response.json();
  }

  // If we're running within the context of the parent window, then initialize
  // the Microsoft Api integration
  init().catch((err) => {
    console.error(err);
  });

  // If we're running within the context of the parent window, then initialize
  // the Microsoft Api integration
  if (!window.location.href.includes(`${config.baseUrl}/gateway/`)) {
    init().catch((err) => {
      console.error(err);
    });
  }
}
